import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';
import Icon from '../../../components/AppIcon';

const VisualizationArea = ({ selectedReport, selectedCategory, dateRange, filters, onExport }) => {
  const [viewType, setViewType] = useState('chart');
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Mock data for different report types
  const mockData = {
    'monthly-sales': [
      { month: 'Ene', ventas: 65000, objetivo: 70000 },
      { month: 'Feb', ventas: 72000, objetivo: 75000 },
      { month: 'Mar', ventas: 68000, objetivo: 70000 },
      { month: 'Abr', ventas: 81000, objetivo: 80000 },
      { month: 'May', ventas: 86000, objetivo: 85000 },
      { month: 'Jun', ventas: 92000, objetivo: 90000 },
      { month: 'Jul', ventas: 85000, objetivo: 88000 }
    ],
    'sales-by-product': [
      { producto: 'Producto A', ventas: 450000, porcentaje: 35 },
      { producto: 'Producto B', ventas: 320000, porcentaje: 25 },
      { producto: 'Producto C', ventas: 280000, porcentaje: 22 },
      { producto: 'Producto D', ventas: 150000, porcentaje: 12 },
      { producto: 'Otros', ventas: 80000, porcentaje: 6 }
    ],
    'stock-levels': [
      { producto: 'Producto A', actual: 450, minimo: 200, maximo: 800 },
      { producto: 'Producto B', actual: 120, minimo: 150, maximo: 600 },
      { producto: 'Producto C', actual: 680, minimo: 300, maximo: 900 },
      { producto: 'Producto D', actual: 340, minimo: 250, maximo: 700 },
      { producto: 'Producto E', actual: 95, minimo: 100, maximo: 500 }
    ],
    'customer-segmentation': [
      { segmento: 'Premium', clientes: 85, ingresos: 450000 },
      { segmento: 'Frecuente', clientes: 156, ingresos: 320000 },
      { segmento: 'Nuevo', clientes: 203, ingresos: 180000 },
      { segmento: 'Ocasional', clientes: 97, ingresos: 120000 }
    ]
  };

  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

  const getChartComponent = () => {
    if (!selectedReport) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Icon name="BarChart3" size={48} className="mx-auto text-text-muted mb-4" />
            <h3 className="text-lg font-heading-semibold text-text-secondary mb-2">
              Selecciona un Reporte
            </h3>
            <p className="text-text-muted">
              Elige un reporte de la lista para visualizar los datos
            </p>
          </div>
        </div>
      );
    }

    const data = mockData[selectedReport.id] || [];

    switch (selectedReport.id) {
      case 'monthly-sales':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => [`€${value.toLocaleString()}`, '']} />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="ventas" 
                stackId="1" 
                stroke={colors[0]} 
                fill={colors[0]} 
                fillOpacity={0.3}
                name="Ventas"
              />
              <Line 
                type="monotone" 
                dataKey="objetivo" 
                stroke={colors[1]} 
                strokeDasharray="5 5"
                name="Objetivo"
              />
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'sales-by-product':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={120}
                dataKey="ventas"
                nameKey="producto"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`€${value.toLocaleString()}`, '']} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'stock-levels':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="producto" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="actual" fill={colors[0]} name="Stock Actual" />
              <Bar dataKey="minimo" fill={colors[3]} name="Mínimo" />
              <Bar dataKey="maximo" fill={colors[1]} name="Máximo" />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'customer-segmentation':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="segmento" />
              <YAxis yAxisId="left" orientation="left" />
              <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`} />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="clientes" fill={colors[0]} name="Clientes" />
              <Bar yAxisId="right" dataKey="ingresos" fill={colors[1]} name="Ingresos" />
            </BarChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="value" stroke={colors[0]} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );
    }
  };

  const getTableData = () => {
    if (!selectedReport || !mockData[selectedReport.id]) return [];
    return mockData[selectedReport.id];
  };

  const renderTableView = () => {
    const data = getTableData();
    if (data.length === 0) return null;

    const columns = Object.keys(data[0]);

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-secondary-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column}
                  className="px-6 py-3 text-left text-xs font-body-semibold text-text-secondary uppercase tracking-wider"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-border">
            {data.map((row, index) => (
              <tr key={index} className="hover:bg-secondary-25">
                {columns.map((column) => (
                  <td key={column} className="px-6 py-4 whitespace-nowrap text-sm text-text-primary">
                    {typeof row[column] === 'number' ? column.includes('ingresos') || column.includes('ventas') 
                        ? `€${row[column].toLocaleString()}`
                        : row[column].toLocaleString()
                      : row[column]
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (!selectedReport) {
    return (
      <div className="card">
        <div className="p-8 text-center">
          <Icon name="BarChart3" size={64} className="mx-auto text-text-muted mb-4" />
          <h3 className="text-xl font-heading-semibold text-text-secondary mb-2">
            Área de Visualización
          </h3>
          <p className="text-text-muted mb-6">
            Selecciona un reporte de la categoría "{selectedCategory}" para visualizar los datos
          </p>
          <div className="flex justify-center space-x-4">
            <button className="inline-flex items-center px-4 py-2 border border-border text-sm font-body-medium rounded-md text-text-secondary bg-surface hover:bg-secondary-50 transition-micro">
              <Icon name="FileText" size={16} className="mr-2" />
              Plantillas
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-body-medium rounded-md text-white bg-primary hover:bg-primary-600 transition-micro">
              <Icon name="Plus" size={16} className="mr-2" />
              Crear Reporte
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`card ${isFullscreen ? 'fixed inset-4 z-50' : ''}`}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-lg font-heading-semibold text-text-primary">
              {selectedReport.name}
            </h2>
            <p className="text-sm text-text-secondary">
              Período: {dateRange.startDate} - {dateRange.endDate}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewType(viewType === 'chart' ? 'table' : 'chart')}
              className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-50 rounded-md transition-micro"
            >
              <Icon name={viewType === 'chart' ? 'Table' : 'BarChart3'} size={16} />
            </button>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-50 rounded-md transition-micro"
            >
              <Icon name={isFullscreen ? 'Minimize' : 'Maximize'} size={16} />
            </button>
            <button
              onClick={() => onExport('pdf')}
              className="p-2 text-text-secondary hover:text-text-primary hover:bg-secondary-50 rounded-md transition-micro"
            >
              <Icon name="Download" size={16} />
            </button>
          </div>
        </div>

        {/* View Toggle */}
        <div className="flex items-center space-x-1">
          <button
            onClick={() => setViewType('chart')}
            className={`px-3 py-1 text-xs font-body-medium rounded transition-micro ${
              viewType === 'chart' ?'bg-primary text-white' :'text-text-secondary hover:text-text-primary hover:bg-secondary-50'
            }`}
          >
            Gráfico
          </button>
          <button
            onClick={() => setViewType('table')}
            className={`px-3 py-1 text-xs font-body-medium rounded transition-micro ${
              viewType === 'table' ?'bg-primary text-white' :'text-text-secondary hover:text-text-primary hover:bg-secondary-50'
            }`}
          >
            Tabla
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {viewType === 'chart' ? getChartComponent() : renderTableView()}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-border bg-secondary-25">
        <div className="flex items-center justify-between text-sm text-text-secondary">
          <span>
            Última actualización: {new Date().toLocaleString('es-ES')}
          </span>
          <div className="flex items-center space-x-4">
            <button className="text-primary hover:text-primary-600 transition-micro">
              Compartir
            </button>
            <button className="text-primary hover:text-primary-600 transition-micro">
              Programar
            </button>
            <button className="text-primary hover:text-primary-600 transition-micro">
              Configurar
            </button>
          </div>
        </div>
      </div>

      {/* Fullscreen overlay */}
      {isFullscreen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-25 z-40"
          onClick={() => setIsFullscreen(false)}
        />
      )}
    </div>
  );
};

export default VisualizationArea;